import { ElementType, Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

import { CourseTypes } from 'config';
import { BANNER_TAB_VALUES } from 'pages/banner/config';
import { CLIENT_TAB_VALUES } from 'pages/client/config';
import { COMMENT_CREATE_TAB_VALUES } from 'pages/comment/config';
import ConstantCreate from 'pages/constants/ConstantCreate';
import { DIET_TAB_VALUES } from 'pages/diet/config';
import DisablePayment from 'pages/disablePayment/DisablePayment';
import { GROUP_TAB_VALUES } from 'pages/group/config';
import { MAIN_TAB_VALUES } from 'pages/main/MainList';
import { PRODUCT_TAB_VALUES } from 'pages/product/config';
import { SOCIALS_TAB_VALUES } from 'pages/socials/config';
import { STORIES_TAB_VALUES } from 'pages/story/config';
import { TRAINER_TAB_VALUES } from 'pages/trainer/config';
import { COMMENT_TAB_VALUES } from 'sections/comment/config';
import { DietTypes } from 'types/diet';
import { LoadingScreen } from '../components/LoadingScreen';
import { AuthGuard } from '../guards/AuthGuard';
import { GuestGuard } from '../guards/GuestGuard';
import { useAuth } from '../hooks/useAuth';
import { DashboardLayout } from '../layouts/dashboard';
import { LogoOnlyLayout } from '../layouts/LogoOnlyLayout';
import Login from '../pages/auth/Login';
import NewPassword from '../pages/auth/NewPassword';
import Register from '../pages/auth/Register';
import VerifyCode from '../pages/auth/VerifyCode';

const Loadable = (title: string, Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Component {...props} />
    </Suspense>
  );
};

const MarathonReviewsComments = Loadable(
  'Отзывы и комментарии',
  lazy(() => import('../pages/comment/MarathonReviewsComments'))
);
const MarathonProductList = Loadable(
  'Продукты марафонов',
  lazy(() => import('../pages/marathonProduct/MarathonProductList'))
);
const MarathonProductCreate = Loadable(
  'Создание продукта марафона',
  lazy(() => import('../pages/marathonProduct/MarathonProductCreate'))
);
const MarathonProductEdit = Loadable(
  'Редактирование продукта марафона',
  lazy(() => import('../pages/marathonProduct/MarathonProductEdit'))
);
const BadList = Loadable(
  'БАДы',
  lazy(() => import('../pages/bad/BadList'))
);
const BadCreate = Loadable(
  'Создание БАДа',
  lazy(() => import('../pages/bad/BadCreate'))
);
const BadEdit = Loadable(
  'Редактирование БАДа',
  lazy(() => import('../pages/bad/BadEdit'))
);

export const Router = () =>
  useRoutes([
    {
      path: '/',
      element: <Navigate to="/dashboard/index" replace />,
    },
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/index" replace />, index: true },
        { path: 'index', element: <Profile /> },
        { path: 'dashboard', element: <Dashboard /> },
        { path: 'downloads', element: <Downloads /> },
        {
          path: 'user',
          children: [
            { element: <UserList />, index: true },
            { path: 'new', element: <UserCreate /> },
            { path: ':id/edit', element: <UserCreate /> },
          ],
        },
        {
          path: 'dictionary_automoderation',
          children: [
            { element: <DictionaryAutoModerationList />, index: true },
            { path: 'new', element: <DictionaryAutoModerationCreate /> },
            { path: ':id/edit', element: <DictionaryAutoModerationCreate /> },
          ],
        },
        {
          path: 'label',
          children: [
            { element: <></>, index: true },
            { path: ':type', element: <LabelDetails /> },
            { path: ':type/new', element: <LabelCreate /> },
            { path: ':id/edit', element: <LabelEdit /> },
          ],
        },
        {
          path: 'referral_programme',
          children: [
            { element: <ReferralProgrammeList />, index: true },
            { path: 'new', element: <ReferralProgrammeCreate /> },
            { path: 'rules', element: <ReferralProgrammeRules /> },
            { path: ':id/edit', element: <ReferralProgrammeCreate /> },
          ],
        },
        {
          path: 'comment',
          children: [
            { element: <Comments tab={COMMENT_TAB_VALUES.REVIEW} />, index: true },
            { path: 'all', element: <Comments tab={COMMENT_TAB_VALUES.ALL} /> },
            { path: 'complaints', element: <Comments tab={COMMENT_TAB_VALUES.COMPLAINTS} /> },
            { path: 'marathon', element: <MarathonReviewsComments tab="reviews" /> },
            {
              path: 'marathon/comments',
              element: <MarathonReviewsComments tab="comments" />,
            },
            {
              path: 'marathon/complaints',
              element: <MarathonReviewsComments tab="complaints" />,
            },
            { path: 'new', element: <CommentsCreate /> },
            { path: ':id/info', element: <CommentsCreate /> },
            {
              path: ':id/info_with_complaints/:complaintId',
              element: <CommentsCreate />,
            },
            {
              path: ':id/applications',
              element: <CommentsCreate />,
            },
          ],
        },
        {
          path: 'story',
          children: [
            { element: <StoryList tab={MAIN_TAB_VALUES.STORIES} />, index: true },
            { path: 'new', element: <StoryCreate /> },
            { path: ':id/info', element: <StoryEdit tab={STORIES_TAB_VALUES.INFO} /> },
            { path: ':id/styling', element: <StoryEdit tab={STORIES_TAB_VALUES.STYLING} /> },
            { path: ':id/stories', element: <StoryEdit tab={STORIES_TAB_VALUES.STORIES} /> },
          ],
        },
        // Пока скрыли за ненадобностью
        // {
        //   path: 'promocode',
        //   children: [
        //     { element: <PromocodeList />, index: true },
        //     { path: 'new', element: <PromocodeCreate /> },
        //     { path: ':id/edit', element: <PromocodeCreate /> },
        //   ],
        // },
        {
          path: 'client',
          children: [
            { element: <ClientList />, index: true },
            { path: 'new', element: <ClientCreate /> },
            { path: ':id/info', element: <ClientCreate tab={CLIENT_TAB_VALUES.INFO} /> },
            { path: ':id/payment', element: <ClientCreate tab={CLIENT_TAB_VALUES.PAYMENT} /> },
            { path: ':id/history', element: <ClientCreate tab={CLIENT_TAB_VALUES.HISTORY} /> },
            { path: ':id/activity', element: <ClientCreate tab={CLIENT_TAB_VALUES.ACTIVITY} /> },
            { path: ':id/achieves-workouts', element: <AchievesWorkouts /> },
          ],
        },
        {
          path: 'trainer',
          children: [
            { element: <TrainerList />, index: true },
            { path: 'new', element: <TrainerCreate /> },
            { path: ':id/info', element: <TrainerEdit tab={TRAINER_TAB_VALUES.INFO} /> },
            { path: ':id/styling', element: <TrainerEdit tab={TRAINER_TAB_VALUES.STYLING} /> },
          ],
        },

        {
          path: 'product',
          children: [
            { element: <ProductList />, index: true },
            { path: 'new/info', element: <ProductCreate tab={PRODUCT_TAB_VALUES.INFO} /> },
            { path: 'new/content', element: <ProductCreate tab={PRODUCT_TAB_VALUES.CONTENT} /> },
            { path: ':id/info', element: <ProductCreate tab={PRODUCT_TAB_VALUES.INFO} /> },
            { path: ':id/content', element: <ProductCreate tab={PRODUCT_TAB_VALUES.CONTENT} /> },
            {
              path: ':id/promotions',
              element: <ProductCreate tab={PRODUCT_TAB_VALUES.PROMOTIONS} />,
            },
            {
              path: ':id/notifications',
              element: <ProductCreate tab={PRODUCT_TAB_VALUES.NOTIFICATIONS} />,
            },
          ],
        },

        {
          path: 'marathon-product',
          children: [
            { element: <MarathonProductList />, index: true },
            { path: 'new', element: <MarathonProductCreate /> },
            { path: ':id/edit', element: <MarathonProductEdit /> },
          ],
        },

        {
          path: 'bad',
          children: [
            { element: <BadList />, index: true },
            { path: 'new', element: <BadCreate /> },
            { path: ':id/edit', element: <BadEdit /> },
          ],
        },

        {
          path: 'promotion',
          children: [
            { element: <PromotionList />, index: true },
            { path: 'new', element: <PromotionCreate /> },
            { path: ':id/edit', element: <PromotionCreate /> },
          ],
        },
        {
          path: 'deep_link',
          children: [{ element: <DeepLink />, index: true }],
        },
        {
          path: 'paywall',
          children: [{ element: <Paywall />, index: true }],
        },
        {
          path: 'gift-box',
          children: [{ element: <GiftBox />, index: true }],
        },
        {
          path: 'gift-acts',
          children: [
            { element: <GiftActsList />, index: true },
            { path: ':id/info', element: <GiftActDetails /> },
          ],
        },
        {
          path: 'carrot',
          children: [{ element: <Carrot />, index: true }],
        },
        {
          path: 'course',
          children: [
            { element: <CourseList courseType={CourseTypes.COURSE} />, index: true },
            { path: 'new', element: <CourseCreate courseType={CourseTypes.COURSE} /> },
            { path: ':id/info', element: <CourseEdit courseType={CourseTypes.COURSE} /> },
          ],
        },
        /** @deprecated Старая структура марафонов, будет заменена */
        {
          path: 'marathon',
          children: [
            { element: <MarathonList courseType={CourseTypes.MARATHON} />, index: true },
            { path: 'new', element: <MarathonCreate courseType={CourseTypes.MARATHON} /> },
            { path: ':id/info', element: <MarathonEdit courseType={CourseTypes.MARATHON} /> },
          ],
        },
        {
          path: 'challenge',
          children: [
            { element: <ChallengeList courseType={CourseTypes.CHALLENGE} />, index: true },
            { path: 'new', element: <ChallengeCreate courseType={CourseTypes.CHALLENGE} /> },
            { path: ':id/info', element: <ChallengeEdit courseType={CourseTypes.CHALLENGE} /> },
          ],
        },
        {
          path: 'workout',
          children: [
            { element: <WorkoutList courseType={CourseTypes.WORKOUT} />, index: true },
            {
              path: 'new',
              element: <WorkoutCreate courseType={CourseTypes.WORKOUT} />,
            },
            {
              path: ':id/info',
              element: <WorkoutEdit courseType={CourseTypes.WORKOUT} />,
            },
          ],
        },
        {
          path: 'tutorial',
          children: [
            { element: <TutorialList courseType={CourseTypes.TUTORIAL} />, index: true },
            {
              path: 'new',
              element: <TutorialCreate courseType={CourseTypes.TUTORIAL} />,
            },
            {
              path: ':id/info',
              element: <TutorialEdit courseType={CourseTypes.TUTORIAL} />,
            },
          ],
        },
        {
          path: 'complex',
          children: [
            { element: <ComplexList courseType={CourseTypes.COMPLEX} />, index: true },
            {
              path: 'new',
              element: <ComplexCreate courseType={CourseTypes.COMPLEX} />,
            },
            {
              path: ':id/info',
              element: <ComplexEdit courseType={CourseTypes.COMPLEX} />,
            },
          ],
        },
        {
          path: 'broadcast',
          children: [
            { element: <BroadcastList courseType={CourseTypes.BROADCAST} />, index: true },
            {
              path: 'new',
              element: <BroadcastCreate courseType={CourseTypes.BROADCAST} />,
            },
            {
              path: ':id/info',
              element: <BroadcastEdit courseType={CourseTypes.BROADCAST} />,
            },
          ],
        },
        {
          path: 'constant',
          children: [
            { element: <ConstantList />, index: true },
            { path: 'new', element: <ConstantCreate /> },
            { path: ':id/edit', element: <ConstantCreate /> },
          ],
        },
        {
          path: 'banner',
          children: [
            { element: <BannerList tab={MAIN_TAB_VALUES.BANNERS} />, index: true },
            { path: 'new', element: <BannerCreate /> },
            { path: ':id/info', element: <BannerEdit tab={BANNER_TAB_VALUES.INFO} /> },
            { path: ':id/styling', element: <BannerEdit tab={BANNER_TAB_VALUES.STYLING} /> },
          ],
        },
        {
          path: 'group',
          children: [
            { element: <GroupsList />, index: true },
            { path: 'new', element: <GroupCreate /> },
            { path: ':id/info', element: <GroupEdit tab={GROUP_TAB_VALUES.INFO} /> },
            { path: ':id/styling', element: <GroupEdit tab={GROUP_TAB_VALUES.STYLING} /> },
            { path: ':id/workouts', element: <GroupEdit tab={GROUP_TAB_VALUES.WORKOUTS} /> },
            { path: ':id/sort', element: <GroupEdit tab={GROUP_TAB_VALUES.SORT} /> },
          ],
        },
        {
          path: 'faq',
          children: [
            { element: <FaqList />, index: true },
            { path: 'new', element: <FaqCreate /> },
            { path: 'new?course_id=:id', element: <FaqCreate /> },
            { path: ':id/edit', element: <FaqCreate /> },
          ],
        },
        {
          path: 'socials',
          children: [
            { element: <SocialsList />, index: true },
            { path: 'new', element: <SocialsCreate /> },
            { path: ':id/info', element: <SocialsEdit tab={SOCIALS_TAB_VALUES.INFO} /> },
            { path: ':id/styling', element: <SocialsEdit tab={SOCIALS_TAB_VALUES.STYLING} /> },
          ],
        },
        {
          path: 'video',
          children: [
            { element: <VideoList />, index: true },
            { path: 'new', element: <VideoCreate /> },
          ],
        },
        {
          path: 'order',
          children: [
            { element: <OrderList />, index: true },
            { path: 'new', element: <OrderCreate /> },
            { path: ':id/edit', element: <OrderEdit /> },
          ],
        },
        {
          path: 'version',
          children: [
            { element: <VersionList />, index: true },
            { path: 'new', element: <VersionCreate /> },
            { path: ':id/edit', element: <VersionCreate /> },
          ],
        },
        {
          path: 'countries',
          children: [
            { element: <CountryList />, index: true },
            { path: 'new', element: <CountryCreate /> },
            { path: ':id/edit', element: <CountryCreate /> },
          ],
        },
        {
          path: 'level',
          children: [
            { element: <LevelList />, index: true },
            { path: 'new', element: <LevelCreate /> },
            { path: ':id/edit', element: <LevelCreate /> },
          ],
        },
        {
          path: 'direction',
          children: [
            { element: <DirectionlList />, index: true },
            { path: 'new', element: <DirectionCreate /> },
            { path: ':id/edit', element: <DirectionCreate /> },
          ],
        },
        {
          path: 'app-filters',
          children: [
            { element: <AppFilterList />, index: true },
            { path: 'new', element: <AppFilterCreate /> },
            { path: ':id/edit', element: <AppFilterEdit /> },
          ],
        },
        {
          path: 'app-search',
          children: [
            { element: <AppSearchList />, index: true },
            { path: 'new', element: <AppSearchCreate /> },
          ],
        },
        {
          path: 'intensity',
          children: [
            { element: <IntensityList />, index: true },
            { path: 'new', element: <IntensityCreate /> },
            { path: ':id/edit', element: <IntensityCreate /> },
          ],
        },
        {
          path: 'notification',
          children: [{ element: <NotificationList />, index: true }],
        },
        {
          path: 'send_push',
          children: [{ element: <SendPush />, index: true }],
        },
        {
          path: 'trigger_notification',
          children: [
            { element: <AgentList />, index: true },
            { path: 'new', element: <AgentCreate /> },
            { path: ':id/edit', element: <AgentCreate /> },
          ],
        },
        {
          path: 'analytics_load',
          children: [{ element: <AnalyticsLoadList />, index: true }],
        },
        {
          path: 'analytics_app',
          children: [{ element: <AnalyticsAppList />, index: true }],
        },
        {
          path: 'analytics_service',
          children: [{ element: <AnalyticsServiceList />, index: true }],
        },
        {
          path: 'analytics_administration_history',
          children: [{ element: <AdministrationHistoryList />, index: true }],
        },
        {
          path: 'usedesk',
          children: [
            { element: <UsedeskList />, index: true },
            { path: 'new', element: <UsedeskCreate /> },
          ],
        },
        {
          path: 'photo',
          children: [{ element: <PhotoList />, index: true }],
        },
        {
          path: 'pdf-bank',
          children: [{ element: <PdfBankList />, index: true }],
        },
        {
          path: 'diet',
          children: [
            { element: <DietList type={DietTypes.DIET} />, index: true },
            { path: 'new', element: <DietCreate type={DietTypes.DIET} /> },
            {
              path: ':id/info',
              element: <DietEdit tab={DIET_TAB_VALUES.INFO} type={DietTypes.DIET} />,
            },
            {
              path: ':id/styling',
              element: <DietEdit tab={DIET_TAB_VALUES.STYLING} type={DietTypes.DIET} />,
            },
          ],
        },
        {
          path: 'pdf',
          children: [
            { element: <DietList type={DietTypes.PDF} />, index: true },
            { path: 'new', element: <DietCreate type={DietTypes.PDF} /> },
            {
              path: ':id/info',
              element: <DietEdit tab={DIET_TAB_VALUES.INFO} type={DietTypes.PDF} />,
            },
            {
              path: ':id/styling',
              element: <DietEdit tab={DIET_TAB_VALUES.STYLING} type={DietTypes.PDF} />,
            },
          ],
        },
        {
          path: 'diet_groups',
          children: [
            { element: <DietGroupsList />, index: true },
            { path: 'sort', element: <DietGroupSort /> },
            { path: 'new', element: <DietGroupCreate /> },
            {
              path: ':id/edit',
              element: <DietGroupEdit />,
            },
          ],
        },
        {
          path: 'achieves',
          children: [
            { element: <AchievesList />, index: true },
            {
              path: ':id/edit',
              element: <AchievesEdit />,
            },
          ],
        },
        {
          path: 'disable_payment',
          children: [{ element: <DisablePayment />, index: true }],
        },
        {
          path: 'global_player',
          children: [{ element: <GlobalPlayer />, index: true }],
        },
        {
          path: 'auth_methods',
          children: [{ element: <AuthMethods />, index: true }],
        },
        {
          path: 'feature_flags',
          children: [
            { element: <FeatureFlagList />, index: true },
            { path: 'new', element: <FeatureFlagCreate /> },
            { path: ':name/edit', element: <FeatureFlagEdit /> },
          ],
        },
        {
          path: 'user-retention',
          children: [
            { element: <UserRetentionList />, index: true },
            { path: 'new', element: <UserRetentionCreate /> },
            { path: ':id/edit', element: <UserRetentionEdit /> },
          ],
        },
        {
          path: 'marathon-template',
          children: [
            { element: <MarathonTemplateList />, index: true },
            { path: 'new', element: <MarathonTemplateCreate /> },
            {
              path: ':id/info',
              element: <MarathonTemplateEdit />,
            },
            {
              path: ':templateId/marathon',
              children: [
                { path: 'new', element: <MarathonLaunchCreate /> },
                { path: ':id/info', element: <MarathonLaunchEdit /> },
                { path: ':id/workouts/new', element: <MarathonWorkoutsCreate /> },
                { path: ':id/workouts/:workoutId/edit', element: <MarathonWorkoutsEdit /> },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);

const Profile = Loadable(
  'Профиль',
  lazy(() => import('../pages/profile/Profile'))
);
const Dashboard = Loadable(
  'Статистика',
  lazy(() => import('../pages/dashboard/Dashboard'))
);
const Downloads = Loadable(
  'Загрузки',
  lazy(() => import('../pages/downloads/Downloads'))
);
const UserList = Loadable(
  'Сотрудники',
  lazy(() => import('../pages/user/UserList'))
);
const UserCreate = Loadable(
  'Сотрудники',
  lazy(() => import('../pages/user/UserCreate'))
);
const ClientList = Loadable(
  'Пользователи',
  lazy(() => import('../pages/client/ClientList'))
);
const ClientCreate = Loadable(
  'Пользователи',
  lazy(() => import('../pages/client/ClientCreate'))
);
const AchievesWorkouts = Loadable(
  'Пользователи',
  lazy(() => import('../pages/AchievesWorkouts/AchievesWorkouts'))
);
const TrainerList = Loadable(
  'Тренеры',
  lazy(() => import('../pages/trainer/TrainerList'))
);
const TrainerCreate = Loadable(
  'Тренеры',
  lazy(() => import('../pages/trainer/TrainerCreate'))
);
const TrainerEdit = Loadable(
  'Тренеры',
  lazy(() => import('../pages/trainer/TrainerEdit'))
);
const ProductList = Loadable(
  'Товары',
  lazy(() => import('../pages/product/ProductList'))
);
const ProductCreate = Loadable(
  'Товары',
  lazy(() => import('../pages/product/ProductCreate'))
);
const WorkoutRoot = lazy(() => import('../pages/workout/WorkoutList'));
const CourseList = Loadable('Курсы', WorkoutRoot);
const WorkoutList = Loadable('Тренировки', WorkoutRoot);
const ComplexList = Loadable('Комплексы', WorkoutRoot);
const BroadcastList = Loadable('Эфиры', WorkoutRoot);
/** @deprecated Старая структура марафонов, будет заменена */
const MarathonList = Loadable('Марафоны', WorkoutRoot);
const ChallengeList = Loadable('Челленджи', WorkoutRoot);
const TutorialList = Loadable('Туториалы', WorkoutRoot);

const WorkoutCreation = lazy(() => import('../pages/workout/WorkoutCreate'));
const WorkoutCreate = Loadable('Создание тренировки', WorkoutCreation);
const ComplexCreate = Loadable('Создание комплекса', WorkoutCreation);
const BroadcastCreate = Loadable('Создание эфира', WorkoutCreation);
const TutorialCreate = Loadable('Создание туториала', WorkoutCreation);

const WorkoutEdition = lazy(() => import('../pages/workout/WorkoutEdit'));
const WorkoutEdit = Loadable('Редактирование тренировки', WorkoutEdition);
const ComplexEdit = Loadable('Редактирование комплекса', WorkoutEdition);
const BroadcastEdit = Loadable('Редактирование эфира', WorkoutEdition);
const TutorialEdit = Loadable('Редактирование туториала', WorkoutEdition);

const CourseCreation = lazy(() => import('../pages/course/CourseCreate'));
const CourseCreate = Loadable('Создание курса', CourseCreation);
/** @deprecated Старая структура марафонов, будет заменена */
const MarathonCreate = Loadable('Создание марафона', CourseCreation);
const ChallengeCreate = Loadable('Создание челленджа', CourseCreation);

const CourseEdition = lazy(() => import('../pages/course/CourseEdit'));
const CourseEdit = Loadable('Редактирование курса', CourseEdition);
/** @deprecated Старая структура марафонов, будет заменена */
const MarathonEdit = Loadable('Редактирование марафона', CourseEdition);
const ChallengeEdit = Loadable('Редактирование челленджа', CourseEdition);

const VideoList = Loadable(
  'Видео',
  lazy(() => import('../pages/video/VideoList'))
);
const VideoCreate = Loadable(
  'Создание видео',
  lazy(() => import('../pages/video/VideoCreate'))
);
const OrderList = Loadable(
  'Счета',
  lazy(() => import('../pages/order/OrderList'))
);
const OrderCreate = Loadable(
  'Создание счета',
  lazy(() => import('../pages/order/OrderCreate'))
);
const OrderEdit = Loadable(
  'Редактирование счета',
  lazy(() => import('../pages/order/OrderEdit'))
);
const NotFound = Loadable(
  '404 Page Not Found',
  lazy(() => import('../pages/Page404'))
);
const ConstantList = Loadable(
  'Константы',
  lazy(() => import('../pages/constants/ConstantList'))
);
const NotificationList = Loadable(
  'Уведомления',
  lazy(() => import('../pages/notification/NotificationList'))
);
const SendPush = Loadable(
  'Уведомления',
  lazy(() => import('../pages/notification/SendPush'))
);
const SocialsList = Loadable(
  'Социальные сети',
  lazy(() => import('../pages/socials/SocialsList'))
);
const SocialsCreate = Loadable(
  'Создание социальной сети',
  lazy(() => import('../pages/socials/SocialsCreate'))
);
const SocialsEdit = Loadable(
  'Редактирование социальной сети',
  lazy(() => import('../pages/socials/SocialsEdit'))
);
const AnalyticsLoadList = Loadable(
  'Аналитика нагрузки',
  lazy(() => import('../pages/analytics/analyticsLoad/AnalyticsLoadList'))
);
const AnalyticsAppList = Loadable(
  'Аналитика приложения',
  lazy(() => import('../pages/analytics/analyticsApp/AnalyticsAppList'))
);
const AnalyticsServiceList = Loadable(
  'Аналитика сервиса',
  lazy(() => import('../pages/analytics/analyticsService/AnalyticsServiceList'))
);
const VersionList = Loadable(
  'Версии',
  lazy(() => import('../pages/version/VersionList'))
);
const VersionCreate = Loadable(
  'Создание версии',
  lazy(() => import('../pages/version/VersionCreate'))
);
const CountryList = Loadable(
  'Страны',
  lazy(() => import('../pages/country/CountryList'))
);
const CountryCreate = Loadable(
  'Создание страны',
  lazy(() => import('../pages/country/CountryCreate'))
);
const LevelList = Loadable(
  'Уровни',
  lazy(() => import('../pages/level/LevelList'))
);
const LevelCreate = Loadable(
  'Создание уровня',
  lazy(() => import('../pages/level/LevelCreate'))
);
const DirectionlList = Loadable(
  'Направления',
  lazy(() => import('../pages/directions/DirectionList'))
);
const AppFilterList = Loadable(
  'Фильтры приложения',
  lazy(() => import('../pages/appFilter/AppFilterList'))
);
const AppFilterCreate = Loadable(
  'Создание фильтра',
  lazy(() => import('../pages/appFilter/AppFilterCreate'))
);
const AppFilterEdit = Loadable(
  'Редактирование фильтра',
  lazy(() => import('../pages/appFilter/AppFilterEdit'))
);
const AppSearchList = Loadable(
  'Поиск в приложении',
  lazy(() => import('../pages/appSearch/AppSearchList'))
);
const AppSearchCreate = Loadable(
  'Добавить подсказку',
  lazy(() => import('../pages/appSearch/AppSearchCreate'))
);
const DirectionCreate = Loadable(
  'Создание направления',
  lazy(() => import('../pages/directions/DirectionCreate'))
);
const IntensityList = Loadable(
  'Интенсивность',
  lazy(() => import('../pages/intensity/IntensityList'))
);
const IntensityCreate = Loadable(
  'Создание интенсивности',
  lazy(() => import('../pages/intensity/IntensityCreate'))
);
const UsedeskCreate = Loadable(
  'UseDesk',
  lazy(() => import('../pages/useDesk/UseDeskCreate'))
);
const UsedeskList = Loadable(
  'UseDesk',
  lazy(() => import('../pages/useDesk/UseDeskList'))
);
const AgentList = Loadable(
  'Агент',
  lazy(() => import('../pages/agent/AgentList'))
);
const AgentCreate = Loadable(
  'Создание агента',
  lazy(() => import('../pages/agent/AgentCreate'))
);
const GroupsList = Loadable(
  'Подборки',
  lazy(() => import('../pages/group/GroupList'))
);
const GroupCreate = Loadable(
  'Создание подборки',
  lazy(() => import('../pages/group/GroupCreate'))
);
const GroupEdit = Loadable(
  'Редактирование подборки',
  lazy(() => import('../pages/group/GroupEdit'))
);
const AdministrationHistoryList = Loadable(
  'История администрирования',
  lazy(() => import('../pages/AdministrationHistory/AdministrationHistoryList'))
);
const Comments = Loadable(
  'Комментарии',
  lazy(() => import('../pages/comment/Comments'))
);
const CommentsCreate = Loadable(
  'Комментарии',
  lazy(() => import('../pages/comment/CommentCreate'))
);
const DictionaryAutoModerationList = Loadable(
  'Словарь автомодерации',
  lazy(() => import('../pages/dictionaryAutoModeration/DictionaryAutoModerationList'))
);
const DictionaryAutoModerationCreate = Loadable(
  'Словарь автомодерации',
  lazy(() => import('../pages/dictionaryAutoModeration/DictionaryAutoModerationCreate'))
);
const ReferralProgrammeList = Loadable(
  'Реферальная программа',
  lazy(() => import('../pages/referralProgramme/ReferralProgrammeList'))
);
const ReferralProgrammeCreate = Loadable(
  'Реферальная программа',
  lazy(() => import('../pages/referralProgramme/ReferralProgrammeCreate'))
);
const ReferralProgrammeRules = Loadable(
  'Правила реферральной программы',
  lazy(() => import('../pages/referralProgramme/ReferralProgrammeRules'))
);
const LabelDetails = Loadable(
  'Лейблы',
  lazy(() => import('../pages/labels/LabelDetails'))
);
const LabelEdit = Loadable(
  'Редактировать лейблы',
  lazy(() => import('../pages/labels/LabelCreate'))
);
const LabelCreate = Loadable(
  'Создать лейблы',
  lazy(() => import('../pages/labels/LabelCreate'))
);
// Пока скрыли за ненадобностью
// const PromocodeCreate = Loadable(
//   'Промокод',
//   lazy(() => import('../pages/promocode/PromocodeCreate'))
// );
// const PromocodeList = Loadable(
//   'Промокод',
//   lazy(() => import('../pages/promocode/PromocodeList'))
// );
const PromotionCreate = Loadable(
  'Акции',
  lazy(() => import('../pages/promotion/PromotionCreate'))
);
const PromotionList = Loadable(
  'Акции',
  lazy(() => import('../pages/promotion/PromotionList'))
);
const DeepLink = Loadable(
  'Диплинки',
  lazy(() => import('../pages/deepLink/DeepLink'))
);
const Paywall = Loadable(
  'Paywall',
  lazy(() => import('../pages/paywall/Paywall'))
);
const GiftBox = Loadable(
  'Подарочный бокс',
  lazy(() => import('../pages/giftBox/GiftBox'))
);
const GiftActsList = Loadable(
  'Акты подарков',
  lazy(() => import('../pages/giftActs/GiftActsList'))
);
const GiftActDetails = Loadable(
  'Акт подарка',
  lazy(() => import('../pages/giftActs/GiftActDetails'))
);
const Carrot = Loadable(
  'Керрот',
  lazy(() => import('../pages/carrot/Carrot'))
);
const FaqCreate = Loadable(
  'Faq',
  lazy(() => import('../pages/faq/FaqCreate'))
);
const FaqList = Loadable(
  'Faq',
  lazy(() => import('../pages/faq/FaqList'))
);

const MainList = lazy(() => import('../pages/main/MainList'));
const BannerList = Loadable('Баннеры', MainList);
const StoryList = Loadable('Баннеры', MainList);

const BannerCreate = Loadable(
  'Создание баннера',
  lazy(() => import('../pages/banner/BannerCreate'))
);
const BannerEdit = Loadable(
  'Редактирование баннера',
  lazy(() => import('../pages/banner/BannerEdit'))
);
const StoryCreate = Loadable(
  'Создание группы историй',
  lazy(() => import('../pages/story/StoryCreate'))
);
const StoryEdit = Loadable(
  'Редактирование группы историй',
  lazy(() => import('../pages/story/StoryEdit'))
);

const PhotoList = Loadable(
  'Фотобанк',
  lazy(() => import('../pages/photo/PhotoList'))
);

const PdfBankList = Loadable(
  'PDF банк',
  lazy(() => import('../pages/pdf/PdfList'))
);

const DietList = Loadable(
  'Питание',
  lazy(() => import('../pages/diet/DietList'))
);
const DietCreate = Loadable(
  'Создание карточки питания',
  lazy(() => import('../pages/diet/DietCreate'))
);
const DietEdit = Loadable(
  'Редактирование карточки питания',
  lazy(() => import('../pages/diet/DietEdit'))
);

const DietGroupsList = Loadable(
  'Питание',
  lazy(() => import('../pages/dietGroups/DietGroupsList'))
);
const DietGroupCreate = Loadable(
  'Создание карточки питания',
  lazy(() => import('../pages/dietGroups/DietGroupCreate'))
);
const DietGroupEdit = Loadable(
  'Редактирование карточки питания',
  lazy(() => import('../pages/dietGroups/DietGroupEdit'))
);
const DietGroupSort = Loadable(
  'Редактирование сортировки подборок питания',
  lazy(() => import('../pages/dietGroups/DietGroupSort'))
);
const AchievesList = Loadable(
  'Достижения',
  lazy(() => import('../pages/achieves/AchievesList'))
);
const AchievesEdit = Loadable(
  'Редактировать достижения',
  lazy(() => import('../pages/achieves/AchieveEdit'))
);
const GlobalPlayer = Loadable(
  'Плеер',
  lazy(() => import('../pages/globalPlayer/GlobalPlayer'))
);
const AuthMethods = Loadable(
  'Авторизация',
  lazy(() => import('../pages/authMethods/AuthMethods'))
);
const FeatureFlagList = Loadable(
  'Фич-флаги',
  lazy(() => import('../pages/featureFlag/FeatureFlagList'))
);
const FeatureFlagCreate = Loadable(
  'Создание фич-флага',
  lazy(() => import('../pages/featureFlag/FeatureFlagCreate'))
);
const FeatureFlagEdit = Loadable(
  'Редактирование фич-флага',
  lazy(() => import('../pages/featureFlag/FeatureFlagEdit'))
);
const UserRetentionList = Loadable(
  'Удержание пользователей',
  lazy(() => import('../pages/userRetention/UserRetentionList'))
);
const UserRetentionCreate = Loadable(
  'Создание группы',
  lazy(() => import('../pages/userRetention/UserRetentionCreate'))
);
const UserRetentionEdit = Loadable(
  'Редактирование группы',
  lazy(() => import('../pages/userRetention/UserRetentionEdit'))
);
const MarathonTemplateList = Loadable(
  'Марафоны',
  lazy(() => import('../pages/marathonTemplate/MarathonTemplateList'))
);
const MarathonTemplateCreate = Loadable(
  'Создание марафона',
  lazy(() => import('../pages/marathonTemplate/MarathonTemplateCreate'))
);
const MarathonTemplateEdit = Loadable(
  'Редактирование марафона',
  lazy(() => import('../pages/marathonTemplate/MarathonTemplateEdit'))
);
const MarathonLaunchCreate = Loadable(
  'Создание перезапуска',
  lazy(() => import('../pages/marathon/MarathonCreate'))
);
const MarathonLaunchEdit = Loadable(
  'Редактирование перезапуска',
  lazy(() => import('../pages/marathon/MarathonEdit'))
);
const MarathonWorkoutsCreate = Loadable(
  'Добавить день',
  lazy(() => import('../pages/marathon/workouts/MarathonWorkoutsCreate'))
);
const MarathonWorkoutsEdit = Loadable(
  'Редактировать день',
  lazy(() => import('../pages/marathon/workouts/MarathonWorkoutsEdit'))
);
